<template>
  <PopoverRoot :open="open" @update:open="open = $event">
    <PopoverAnchorRoot as-child>
      <span
        @click="handleTriggerClick"
        @mouseenter="handleTriggerMouseEnter"
        @mouseleave="handleTriggerMouseLeave"
        @focusin="handleTriggerFocusIn"
        @focusout="handleTriggerFocusOut"
      >
        <slot />
      </span>
    </PopoverAnchorRoot>
    <PopoverContentRoot
      :side="side"
      :align="align"
      :side-offset="8"
      @mouseenter="handleContentMouseEnter"
      @mouseleave="handleContentMouseLeave"
    >
      <div v-if="title" class="tw-mb-4">
        {{ title }}
      </div>
      <slot name="popover" />
    </PopoverContentRoot>
  </PopoverRoot>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'

import {
  Popover as PopoverRoot,
  PopoverAnchor as PopoverAnchorRoot,
  PopoverContent as PopoverContentRoot,
} from '@/componentes/ui/popover'

const HOVER_CLOSE_DELAY = 120

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: '',
  },
  placement: {
    type: String,
    required: false,
    default: 'top',
  },
  trigger: {
    type: String,
    required: false,
    default: 'click',
  },
})

const open = ref(false)
const closeTimeoutId = ref(0)

const isHoverTrigger = computed(() => {
  return props.trigger === 'hover' || props.trigger === 'hover-focus'
})

const side = computed(() => {
  const value = (props.placement || 'top').split(' ')[0]

  if (['top', 'right', 'bottom', 'left'].includes(value)) {
    return value
  }

  return 'top'
})

const align = computed(() => {
  const parts = (props.placement || '').split(' ')
  const value = parts[1]

  if (['start', 'center', 'end'].includes(value)) {
    return value
  }

  return 'center'
})

const clearPendingClose = () => {
  if (!closeTimeoutId.value) {
    return
  }

  clearTimeout(closeTimeoutId.value)
  closeTimeoutId.value = 0
}

const openPopover = () => {
  clearPendingClose()
  open.value = true
}

const scheduleClose = () => {
  clearPendingClose()
  closeTimeoutId.value = window.setTimeout(() => {
    open.value = false
    closeTimeoutId.value = 0
  }, HOVER_CLOSE_DELAY)
}

const handleTriggerMouseEnter = () => {
  if (!isHoverTrigger.value) {
    return
  }

  openPopover()
}

const handleTriggerMouseLeave = () => {
  if (!isHoverTrigger.value) {
    return
  }

  scheduleClose()
}

const handleTriggerFocusIn = () => {
  if (props.trigger !== 'hover-focus') {
    return
  }

  openPopover()
}

const handleTriggerFocusOut = () => {
  if (props.trigger !== 'hover-focus') {
    return
  }

  scheduleClose()
}

const handleTriggerClick = () => {
  if (isHoverTrigger.value) {
    return
  }

  clearPendingClose()
  open.value = !open.value
}

const handleContentMouseEnter = () => {
  if (!isHoverTrigger.value) {
    return
  }

  openPopover()
}

const handleContentMouseLeave = () => {
  if (!isHoverTrigger.value) {
    return
  }

  scheduleClose()
}

onBeforeUnmount(() => {
  clearPendingClose()
})
</script>
